import { LogoCF } from "./ui";

// O rodapé do hub — aplicativo, ajuda e assinatura da marca.
//
// Uma peça só para as três telas internas, em duas variantes: clara (tela
// inicial, curso) e sobre navy (a Faculdade, que é escura por inteiro). Antes
// cada tela repetia a sua própria linha de "Contabilidade Facilitada · Conta com
// a gente!", e três cópias divergem na primeira vez que alguém mexe em uma.
//
// **Endereços conferidos em 12/08/2026, e não copiados de memória.** Dois
// detalhes que só apareceram porque foram testados:
//
// 1. O "Fale conosco" leva `/site/` **com barra final**. Sem ela o servidor
//    responde 404 — a página funciona no navegador porque o corpo do 404 tem um
//    `window.location` para o WhatsApp, mas um 404 é 404: quebra em pré-carga de
//    link, em bot de verificação e em qualquer cliente que respeite status. Com
//    a barra o mesmo conteúdo vem como 200.
// 2. A URL do Play veio com `&pli=1`, que é estado de navegação do Google e não
//    faz parte do endereço do app. Sem ele o destino é o mesmo.
//
// O FAQ é o único item sem link, de propósito: a tela vai existir aqui dentro do
// hub e ainda não existe. Mesma regra dos cards — "em breve" honesto em vez de
// link quebrado.
export const APP_STORE = "https://apps.apple.com/br/app/contabilidade-facilitada/id6740584358";
export const PLAY_STORE =
  "https://play.google.com/store/apps/details?id=io.kodular.victor.ContabilidadeFacilitada";
export const FALE_CONOSCO = "https://ec.cfaci.me/site/";

export default function Rodape({ sobreNavy = false }: { sobreNavy?: boolean }) {
  const t = sobreNavy
    ? {
        borda: "border-navy-ink/10",
        titulo: "text-navy-ink",
        texto: "text-navy-mute",
        link: "text-navy-ink hover:text-laranja-claro",
        loja: "border-navy-ink/20 text-navy-ink hover:border-laranja hover:bg-navy-ink/5",
        chip: "bg-navy-ink/10 text-navy-mute",
      }
    : {
        borda: "border-borda",
        titulo: "text-titulo",
        texto: "text-cinza",
        link: "text-link hover:text-laranja-texto",
        loja: "border-borda text-titulo hover:border-laranja hover:bg-laranja/8",
        chip: "bg-chip text-chip-tinta",
      };

  return (
    <footer className={`mt-16 border-t ${t.borda} pt-8 pb-4`}>
      <div className="flex flex-wrap items-start justify-between gap-8">
        <div>
          <h2 className={`font-display text-sm font-semibold ${t.titulo}`}>
            Leve o seu curso no celular
          </h2>
          <p className={`mt-1 text-sm ${t.texto}`}>
            O aplicativo do Contabilidade Facilitada, para assistir de onde estiver.
          </p>
          <div className="mt-3 flex flex-wrap gap-2.5">
            <BotaoLoja
              href={APP_STORE}
              classe={t.loja}
              rotulo="App Store"
              sub="para iPhone e iPad"
              icone={<IconeApple />}
            />
            <BotaoLoja
              href={PLAY_STORE}
              classe={t.loja}
              rotulo="Google Play"
              sub="para Android"
              icone={<IconePlay />}
            />
          </div>
        </div>

        <div>
          <h2 className={`font-display text-sm font-semibold ${t.titulo}`}>Ajuda</h2>
          <ul className={`mt-2 flex flex-col gap-1 text-sm ${t.texto}`}>
            <li className="flex min-h-9 items-center gap-2">
              <span>Perguntas frequentes</span>
              <span
                className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.chip}`}
              >
                Em breve
              </span>
            </li>
            <li className="flex min-h-9 items-center">
              <a
                href={FALE_CONOSCO}
                target="_blank"
                rel="noreferrer"
                className={`font-semibold underline underline-offset-4 transition ${t.link}`}
              >
                Fale conosco
                <span className="sr-only"> (abre em outra aba)</span>
              </a>
            </li>
          </ul>
        </div>

        <div className="flex flex-col items-start gap-2">
          {sobreNavy ? (
            <LogoCF className="h-6" />
          ) : (
            // O logo do pacote é branco, feito para fundo azul. Sobre fundo
            // claro ele desaparece, então aqui a marca é o próprio nome.
            <span className={`font-display text-sm font-semibold ${t.titulo}`}>
              Contabilidade Facilitada
            </span>
          )}
          <p className={`text-sm ${t.texto}`}>Conta com a gente!</p>
        </div>
      </div>
    </footer>
  );
}

function BotaoLoja({
  href,
  classe,
  rotulo,
  sub,
  icone,
}: {
  href: string;
  classe: string;
  rotulo: string;
  sub: string;
  icone: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex min-h-11 items-center gap-2.5 rounded-full border px-4 py-2 transition ${classe}`}
    >
      {icone}
      <span className="leading-tight">
        <span className="block font-display text-sm font-semibold">{rotulo}</span>
        <span className="block text-xs opacity-70">{sub}</span>
      </span>
      <span className="sr-only">(abre em outra aba)</span>
    </a>
  );
}

function IconeApple() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-5 shrink-0" aria-hidden="true">
      <path d="M16.4 12.7c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.7-.8-2.9-.8-1.5 0-2.9.9-3.6 2.2-1.6 2.7-.4 6.7 1.1 8.9.7 1.1 1.6 2.3 2.8 2.2 1.1 0 1.5-.7 2.9-.7 1.3 0 1.7.7 2.9.7 1.2 0 2-1.1 2.7-2.2.5-.8.8-1.6 1-2-2.1-.8-2.3-3.9-2.3-4zM14.2 5.9c.6-.7 1-1.7.9-2.7-.9.1-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6 1 .1 2-.5 2.5-1.2z" />
    </svg>
  );
}

/**
 * Triângulo de "play", e não o logotipo do Google Play.
 *
 * A primeira versão tentava reproduzir as quatro faces do logo em um path só, e
 * o resultado eram formas se sobrepondo — um logo malfeito comunica menos que um
 * símbolo simples. O rótulo ao lado já diz "Google Play · para Android"; o ícone
 * é reforço, não identificação.
 */
function IconePlay() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-5 shrink-0" aria-hidden="true">
      <path d="M7.5 4.2c0-.6.7-1 1.2-.7l10.4 7.7c.4.3.4.9 0 1.2L8.7 20.5c-.5.3-1.2-.1-1.2-.7V4.2z" />
    </svg>
  );
}
